import { useState } from "react";
import {
  Archive,
  CircleUserRound,
  Ellipsis,
  Kanban,
  Lock,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Paperclip,
  Plus,
  Settings,
  SlidersHorizontal,
  Trash2,
  UserRound,
  Users,
  ShieldCheck,
} from "lucide-react";
import { APP_NAME, APP_TAGLINE } from "@brainlog/shared";
import { cn } from "@/lib/utils";
import { useUi, openAdmin, openArchive, openEverywhere, openSettings, openView, useCurrentWorkspaceId } from "@/lib/store";
import { useQueryClient } from "@tanstack/react-query";
import { setArchived } from "@/features/archive/ArchiveView";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWorkspaces } from "@/lib/queries";
import type { User, Workspace } from "@/lib/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CreateNotebookDialog, CreateProjectDialog } from "@/features/workspace/CreateProjectDialog";
import { DeleteWorkspaceDialog } from "@/features/workspace/DeleteWorkspaceDialog";
import { signOut } from "@/features/settings/AccountSettings";

function initials(name: string) {
  return name
    .replace(/\(.*?\)/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

export function Rail({ user, collapsed, narrow }: { user: User; collapsed: boolean; narrow?: boolean }) {
  const workspaces = useWorkspaces();
  const current = useCurrentWorkspaceId();
  const setWorkspace = useUi((s) => s.setWorkspace);
  const toggleRail = useUi((s) => s.toggleRail);
  const page = useUi((s) => s.page);
  const [create, setCreate] = useState<null | "notebook" | "project">(null);
  const [deleting, setDeleting] = useState<Workspace | null>(null);
  const [archiving, setArchiving] = useState<Workspace | null>(null);
  const qc = useQueryClient();
  // Archived ones are in the Archive, not here.
  const active = workspaces.data?.filter((w) => !w.archivedAt) ?? [];
  const notebooks = active.filter((w) => w.kind === "notebook");
  const projects = active.filter((w) => w.kind === "project");

  const withTooltip = (label: string, el: React.ReactElement, key?: string) =>
    collapsed ? (
      <Tooltip key={key}>
        <TooltipTrigger asChild>{el}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    ) : (
      el
    );

  const item = (w: Workspace) => {
    const active = w.id === current && !page;
    const shared = w.memberCount > 1;
    const btn = (
      <button
        type="button"
        onClick={() => setWorkspace(w.id)}
        aria-current={active ? "true" : undefined}
        className={cn(
          "flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-rail-foreground/85 hover:bg-white/10 hover:text-rail-foreground",
          active && "bg-white/15 font-semibold text-rail-foreground",
          !collapsed && "pr-8",
        )}
      >
        {/* Collapsed, the rail only has room for initials. */}
        {collapsed && (
          <span aria-hidden className="flex size-7 shrink-0 items-center justify-center rounded-md bg-white/10 text-[0.7rem] font-bold">
            {initials(w.name) || "·"}
          </span>
        )}
        <span className={cn("truncate", collapsed && "sr-only")}>{w.name}</span>
        {!collapsed && (shared || w.encryptionScopeId) && (
          <span className="ml-auto flex shrink-0 items-center gap-1 opacity-70">
            {shared && <Users className="size-3.5" aria-label={`shared with ${w.memberCount - 1} other${w.memberCount === 2 ? "" : "s"}`} />}
            {w.encryptionScopeId && <Lock className="size-3.5" aria-label="end-to-end encrypted" />}
          </span>
        )}
      </button>
    );
    if (collapsed) return withTooltip(w.name, btn, w.id);
    return (
      <div key={w.id} className="group relative">
        {btn}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions for ${w.name}`}
              className="absolute top-1/2 right-1 -translate-y-1/2 rounded p-1 text-rail-foreground/70 opacity-0 group-hover:opacity-100 hover:bg-white/15 focus-visible:opacity-100 data-[state=open]:opacity-100"
            >
              <Ellipsis className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56">
            <DropdownMenuItem onSelect={() => openView("settings", w.id, "Settings")}>
              <SlidersHorizontal /> Settings & sharing
            </DropdownMenuItem>
            {w.role === "owner" && (
              <>
                <DropdownMenuItem onSelect={() => setArchiving(w)}>
                  <Archive /> Archive {w.kind === "notebook" ? "notebook" : "project"}…
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(w)}>
                  <Trash2 /> Delete {w.kind === "notebook" ? "notebook" : "project"}…
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const railButton = (label: string, icon: React.ReactNode, onClick: () => void, active = false, disabled = false) =>
    withTooltip(
      label,
      <button
        key={label}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-rail-foreground/85 hover:bg-white/10 hover:text-rail-foreground disabled:opacity-40 disabled:hover:bg-transparent",
          active && "bg-white/15 font-semibold text-rail-foreground",
        )}
      >
        <span aria-hidden className="flex size-7 shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className={cn("truncate", collapsed && "sr-only")}>{label}</span>
      </button>,
      label,
    );

  const sectionLabel = (label: string) => (
    <p className={cn("px-2 pt-3 text-[0.68rem] font-semibold uppercase tracking-wider text-rail-foreground/60 first:pt-1", collapsed && "sr-only")}>{label}</p>
  );

  // Everything across all notebooks and projects (filter by notebook or project on the page).
  const tool = (name: "files" | "backlog" | "review", label: string, icon: React.ReactNode) => railButton(label, icon, () => openEverywhere(name), page?.name === name);

  return (
    <div className={cn("flex h-full shrink-0 flex-col bg-rail py-2 text-rail-foreground", collapsed ? "w-14 px-1.5" : "w-56 px-2")}>
      <div className={cn("mb-2 flex items-center gap-2 px-1", collapsed && "flex-col gap-1")}>
        <img src="/icon-192.png" alt="" aria-hidden className="size-8 shrink-0" />
        <div className={cn("min-w-0 leading-tight", collapsed && "sr-only")}>
          <p className="truncate text-sm font-semibold tracking-tight">{APP_NAME}</p>
          <p className="truncate text-[0.7rem] text-rail-foreground/65">{APP_TAGLINE}</p>
        </div>
        {!narrow && (
          <button
            type="button"
            onClick={toggleRail}
            className="ml-auto rounded p-1 text-rail-foreground/80 hover:bg-white/10"
            aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
          >
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        )}
      </div>
      <div className="thin-scroll relative flex-1 space-y-1 overflow-y-auto">
        <nav aria-label="Notebooks" className="space-y-1">
          {sectionLabel("Notebooks")}
          {notebooks.map(item)}
          {railButton("New notebook", <Plus className="size-4" />, () => setCreate("notebook"))}
        </nav>
        <nav aria-label="Projects" className="space-y-1">
          {sectionLabel("Projects")}
          {projects.map(item)}
          {railButton("New project", <Plus className="size-4" />, () => setCreate("project"))}
        </nav>
      </div>
      <div className="space-y-1 border-t border-white/10 pt-2">
        <nav aria-label="All notebooks and projects" className="space-y-1">
          <p className={cn("truncate px-2 text-[0.68rem] font-semibold uppercase tracking-wider text-rail-foreground/60", collapsed && "sr-only")} aria-hidden>
            All notebooks & projects
          </p>
          {tool("backlog", "Backlog", <Kanban className="size-4" />)}
          {tool("files", "Files", <Paperclip className="size-4" />)}
        </nav>
        <div className="my-1 border-t border-white/10" />
        {railButton("Archive", <Archive className="size-4" />, () => openArchive(), page?.name === "archive")}
        {railButton("Settings", <Settings className="size-4" />, () => openSettings(), page?.name === "settings" && page.params?.tab !== "admin")}
        {user.isAdmin && railButton("Administration", <ShieldCheck className="size-4" />, () => openAdmin(), page?.name === "settings" && page.params?.tab === "admin")}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Account: ${user.displayName}`}
              className={cn("flex w-full items-center gap-2 truncate rounded-md px-2 py-1 text-left text-[0.75rem] text-rail-foreground/70 hover:bg-white/10", collapsed && "justify-center")}
            >
              <CircleUserRound className="size-4 shrink-0" aria-hidden />
              <span className={cn("truncate", collapsed && "sr-only")}>{user.displayName}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end">
            <DropdownMenuItem onSelect={() => openSettings({ section: "account" })}>
              <UserRound /> Account and plan
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void signOut()}>
              <LogOut /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CreateNotebookDialog open={create === "notebook"} onOpenChange={(o) => !o && setCreate(null)} />
      <CreateProjectDialog open={create === "project"} onOpenChange={(o) => !o && setCreate(null)} />
      {deleting && <DeleteWorkspaceDialog workspace={deleting} open onOpenChange={(o) => !o && setDeleting(null)} />}
      <AlertDialog open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Archive “{archiving?.name}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              It moves to the Archive (above Settings). You{(archiving?.memberCount ?? 1) > 1 ? " and its members" : ""} can still open, search and export it, but nothing can change until you unarchive it. Its automatic tasks, Zotero or Mendeley sync
              and email address pause.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const w = archiving!;
                if (await setArchived(qc, { type: "workspace", id: w.id, name: w.name }, true)) {
                  // Back to an active notebook or project if it was the one open.
                  if (w.id === current) {
                    const next = active.find((x) => x.id !== w.id);
                    if (next) setWorkspace(next.id);
                  }
                }
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
